import { type FC } from "react";
import classes from "./ErrorOverlay.module.less";
import { isPositiveScanResult, type OriginCheckResult } from "~/ScanDMC/models/ScanDMC";
import Modal from "~/common/components/Modal";
import ScanResultAlert from "../ScanResultAlert/ScanResultAlert";
import { Button } from "~/common/components/Button/Button";
import CustomModalHeader from "../CustomModalHeader/CustomModalHeader";
import { useTranslation } from "react-i18next";
import { IMG_EMPTY_STATE } from "~/utils/url/staticResources";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { BackLink, Link } from "~/common/components/Link";
import classNames from "classnames";

interface ErrorOverlayProps {
  onReturn: () => void;
  onContactUs: () => void;
  scanResult: OriginCheckResult;
}

const ErrorOverlay: FC<ErrorOverlayProps> = ({ scanResult, onReturn, onContactUs }) => {
  const { t } = useTranslation();

  const productIsUnavailable = isPositiveScanResult(scanResult) && !scanResult.productCode;

  let footer;
  if (productIsUnavailable) {
    footer = (
      <div className={classes.unavailableProductFooter}>
        <Button fullWidth onClick={onContactUs}>
          {t("page.scandmc.unavailable.button.contactUs")}
        </Button>
        <Link className={classes.unavailableMessageGoBack} href={HOMEPAGE}>
          {t("page.scandmc.unavailable.button.home")}
        </Link>
      </div>
    );
  } else {
    footer = (
      <Button onClick={onReturn} fullWidth>
        {t("page.scandmc.form.button.close")}
      </Button>
    );
  }

  return (
    <Modal
      headerOverride={<CustomModalHeader />}
      footerChildren={footer}
      onClose={onReturn}
    >
      {productIsUnavailable && (
        <BackLink className={classes.backLink} onClick={onReturn}>
          {t("page.scandmc.back")}
        </BackLink>
      )}
      <div className={classes.container}>
        <ScanResultAlert scanResult={scanResult.type} openOverlay={onContactUs} />
        {productIsUnavailable && <ProductUnavailableMessage />}
      </div>
    </Modal>
  );
};

const ProductUnavailableMessage = () => {
  const { t } = useTranslation();
  return (
    <div className={classes.unavailableMessage}>
      <p className={classes.unavailableMessageText}>{t("page.scandmc.unavailable.message")}</p>
      <img src={IMG_EMPTY_STATE} alt={t("page.scandmc.unavailable.message")} />
    </div>
  );
};

export default ErrorOverlay;
